import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { serviceActions } from 'store/redux-store/global-search/async-actions';
import { FormProvider, useForm } from 'react-hook-form';
import { SearchResultsGrid } from '../SearchResultsGrid';
import { initialFacilityAggregateSearch } from 'app/ComponentLibrary/Filter/FacilitySearch/Index';
import { GlobalSearchGridType, SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import { selectSearchResults } from 'store/redux-store/global-search/selectors';
import { selectGridState } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { DrawerFitted } from '../../Common/Drawer/Drawer';
import { SearchDrawerContent } from '../SearchResultsPage';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { GenericTabs } from '../../Common/Tabs/GenericTabs';
import { MarginOrdersFilter } from './MarginOrdersFilter';
import { TargetMarginSearchFilters } from '../TargetMargins/TargetMarginSearchFilters';
import { Chip, Box, Paper, useTheme, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { getMarginFilterRequest } from './MarginFilterUtils';
import { updateTreeNodes, pruneTree } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import { candidateSearchFilterName } from '../Candidate/SearchUtils';
import { gridStateActions } from '../../Common/Grid/GridStateManagement/GridState.redux';
import { PositionOpts } from '../Order/FilterUtils';

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: 'calc(100vh - 105px)', 
    padding: '12px',
  },
  mainPanelRoot: {
    width: '100%',
  },
  container: {
    flexWrap: 'nowrap',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

interface MarginSearchWrapperProps {
  searchString: string;
  gridTag: string;
  loading: boolean;
  getSearchResults: (params: unknown) => void;
  gridType: GlobalSearchGridType;
}


// Component for Orders tab with filters
const OrdersTabContent: React.FC<{
  searchString: string;
  gridTag: string;
  gridType: GlobalSearchGridType;
  loading: boolean;
  getSearchResults: (params: unknown) => void;
  t: (key: string) => string;
}> = ({ searchString, gridTag, gridType, loading, getSearchResults, t }) => {
  const methods = useForm({
    defaultValues: {
      payPackageStatus: [
        {
          id: 0,
          name: 'Active',
          value: 1,
        },
      ],
      positions: PositionOpts[1],
    },
  });
  const dispatch = useDispatch();
  const gridState = useSelector(selectGridState);
  
  // Store current filter request to reuse when sorting/paginating
  const currentFilterRef = React.useRef<any>({});
  
  React.useEffect(() => {
    dispatch(globalSearchActions.clearSearchResults());
  }, [dispatch]);
  
  // Initialize grid state for margin filters
  React.useEffect(() => {
    dispatch(gridStateActions.setGrid(gridTag));
    dispatch(gridStateActions.setFiltersExpanded(true));
  }, [dispatch, gridTag]);
  
  const handleApplyFilter = (filterValues: any, selectedChipFilters?: any) => {
    // Apply default filters if no filters are set
      const hasFilters = Object.keys(filterValues).some(key => {
        const value = filterValues[key];
      return value && (
        (Array.isArray(value) && value.length > 0) ||
        (!Array.isArray(value) && value !== null && value !== undefined)
      );
    });

    // Always include default filters so they show up as chips
    const defaultFilters = {
      payPackageStatus: [
        {
          id: 0,
          name: 'Active',
          value: 1,
        },
      ],
      positions: PositionOpts[1],
    };
    
    const finalFilterValues = {
      ...defaultFilters,
      ...filterValues,
    };

    // Create selectedChipFilters from form values if not provided
    // Only include values that are explicitly set by the user, not default values
    // For margin search, we don't want to mark chips as selected by default
    const finalSelectedChipFilters = selectedChipFilters || {};
    
    // Update Redux state with filter data (this is what SearchResultsChips needs)
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(finalFilterValues),
        selectedChipFilters: finalSelectedChipFilters,
        associatedGridName: gridState.gridTag,
      }),
    );
    
    // Transform filter values to API request format
    const filterRequest = getMarginFilterRequest(finalFilterValues, finalSelectedChipFilters);
    
    // Store the current filter for reuse when sorting/paginating
    currentFilterRef.current = filterRequest;
    
    // Trigger search with filters
    getSearchResults({
      pageNumber: 1,
      pageSize: 250,
      sortModel: { column: 'orderId', direction: 'desc' },
      multiSortedColumns: [],
      filter: filterRequest,
    });
  };

  // Custom getSearchResults function that applies current filters when sorting/paginating
  const handleGridSearchResults = (params: any) => {
    getSearchResults({
      ...params,
      filter: currentFilterRef.current,
    });
  };

  // Trigger initial search with default filters
  React.useEffect(() => {
    // Trigger search with empty filters (which will apply defaults)
    handleApplyFilter({});
  }, []); // Run only once on mount

  return (
    <FormProvider {...methods}>
      <Box id="details-page-paper" sx={{ minHeight: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column' }}>
        <Box id="details-page-filters">
          <MarginOrdersFilter onApplyFilter={handleApplyFilter} />
        </Box>
        {/* Match filters container spacing: keep equal gutters on both sides */}
        <Box sx={{ marginTop: 2, paddingLeft: 0, paddingRight: 0 }}>
          <SearchResultsGrid
            searchCategory={SearchType.orderpaypackage}
            searchString={searchString}
            gridTitle={t('search.globalSearch.order.gridTitle')}
            gridTag={gridTag}
            gridType={GlobalSearchGridType[gridType]}
            loading={loading}
            getSearchResults={handleGridSearchResults}
          />
        </Box>
      </Box>
    </FormProvider>
  );
};

// Simple chip component that reads from form values
const TargetMarginChips: React.FC<{ onRemove: (field: string) => void; selectedChipFilters: any; handleChipClick: (filterKey: string, chipValue: any) => void }> = ({ onRemove, selectedChipFilters, handleChipClick }) => {
  const { watch, setValue, getValues } = useFormContext();
  const marginType = watch('marginType');
  const division = watch('division');
  const targetMarginSource = watch('targetMarginSource');
  const facility = watch('facility');
  const orderId = watch('orderId');
  const skillsetFilter = watch('skillsetFilter');
  const positions = watch('positions');
  const states = watch('states');
  const defaultTargetMargin = watch('defaultTargetMargin');
  const defaultWeeklyPay = watch('defaultWeeklyPay');
  const payRangeMinMaxPercentage = watch('payRangeMinMaxPercentage');

  const getSelectedSkillsetPaths = (filter: any): { name: string; value: string }[] => {
    if (!filter?.skillsets || !Array.isArray(filter.skillsets)) return [];

    const paths: { name: string; value: string }[] = [];

    const traverse = (node: any, path: string[]) => {
      const currentPath = [...path, node.name].filter(Boolean);
      const isSelected = !!node.userChecked;
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;

      if (isSelected && (!hasChildren || !node.children.some((c: any) => c.userChecked))) {
        paths.push({ name: currentPath.join('-'), value: String(node.value) });
      }

      if (hasChildren) {
        node.children.forEach((child: any) => traverse(child, currentPath));
      }
    };

    filter.skillsets.forEach((root: any) => traverse(root, []));
    return paths;
  };

  const skillSetPaths = getSelectedSkillsetPaths(skillsetFilter);

  const hasSkillsetSelection = (filter: any): boolean => {
    if (!filter?.skillsets || !Array.isArray(filter.skillsets)) return false;
    let selected = false;
    const walk = (n: any) => {
      if (selected) return;
      if (n?.userChecked) selected = true;
      if (Array.isArray(n?.children)) n.children.forEach(walk);
    };
    filter.skillsets.forEach(walk);
    return selected;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      flexWrap: 'wrap', 
      marginBottom: 2,
      width: '100%',
      '& .MuiChip-root': { height: 'auto', maxWidth: 'none' },
      '& .MuiChip-label': { whiteSpace: 'normal', textOverflow: 'unset', overflow: 'visible' }
    }}>
      {marginType && (
        <Chip
          label={`Type: ${marginType}`}
          onClick={() => handleChipClick('marginType', marginType)}
          onDelete={() => {
            setValue('marginType', 'Order');
            onRemove('marginType');
          }}
          variant={selectedChipFilters.marginType?.some((selected: any) => selected === marginType) ? 'filled' : 'outlined'}
          color={selectedChipFilters.marginType?.some((selected: any) => selected === marginType) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}          
        />
      )}
      {targetMarginSource && targetMarginSource.length > 0 && targetMarginSource.map((src: any, index: number) => (
        <Chip
          key={`targetMarginSource-${src.id || src.value || index}`}
          label={`Source: ${src.name}`}
          onClick={() => handleChipClick('targetMarginSource', src)}
          onDelete={() => {
            const remaining = targetMarginSource.filter((s: any) => (s.id ?? s.value) !== (src.id ?? src.value));
            setValue('targetMarginSource', remaining);
            onRemove('targetMarginSource');
          }}
          variant={selectedChipFilters.targetMarginSource?.some((selected: any) => (selected.id ?? selected.value) === (src.id ?? src.value)) ? 'filled' : 'outlined'}
          color={selectedChipFilters.targetMarginSource?.some((selected: any) => (selected.id ?? selected.value) === (src.id ?? src.value)) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}    
        />
      ))}
      {division && division.length > 0 && division.map((d: any, index: number) => (
        <Chip
          key={`division-${d.id}-${index}`}
          label={`${d.name}`}
          onClick={() => handleChipClick('division', d)}
          onDelete={() => {
            setValue('division', division.filter((item: any) => item.id !== d.id));
            onRemove('division');
          }}
          variant={selectedChipFilters.division?.some((selected: any) => selected.id === d.id) ? 'filled' : 'outlined'}
          color={selectedChipFilters.division?.some((selected: any) => selected.id === d.id) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      ))}
      {(() => {
        const facilitySelections = Array.isArray(facility?.selections) ? facility.selections : (Array.isArray(facility) ? facility : []);
        return facilitySelections && facilitySelections.length > 0 ? facilitySelections.map((f: any, index: number) => (
          <Chip
            key={`facility-${f.id || f.fid || f.value}-${index}`}
            label={`Facility: ${f.name || f.facilityName || f.value}`}
            onClick={() => handleChipClick('facility', f)}
            onDelete={() => {
              setValue('facility', initialFacilityAggregateSearch);
              onRemove('facility');
            }}
            variant={selectedChipFilters.facility?.some((selected: any) => selected.id === f.id || selected.fid === f.fid || selected.value === f.value) ? 'filled' : 'outlined'}
            color={selectedChipFilters.facility?.some((selected: any) => selected.id === f.id || selected.fid === f.fid || selected.value === f.value) ? 'secondary' : 'default'}
            deleteIcon={<CloseIcon />}
            sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}
          />
        )) : null;
      })()}
      {orderId && orderId.length > 0 && orderId.map((o: any, index: number) => (
        <Chip
          key={`orderId-${o.id || o.value}-${index}`}
          label={`OID: ${o.name || o.value}`}
          onClick={() => handleChipClick('orderId', o)}
          onDelete={() => {
            setValue('orderId', orderId.filter((item: any) => item.id !== o.id && item.value !== o.value));
            onRemove('orderId');
          }}
          variant={selectedChipFilters.orderId?.some((selected: any) => selected.id === o.id || selected.value === o.value) ? 'filled' : 'outlined'}
          color={selectedChipFilters.orderId?.some((selected: any) => selected.id === o.id || selected.value === o.value) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      ))}
      {skillSetPaths.length > 0 && skillSetPaths.map((skill: { name: string; value: string }, index: number) => (
        <Chip
          key={`skillSet-${skill.value}-${index}`}
          label={`Skill Set: ${skill.name}`}
          onClick={() => handleChipClick('skillSet', { name: skill.name, value: skill.value })}
          onDelete={() => {
            // Remove only this leaf from the tree
            const current = getValues('skillsetFilter');
            const cloned = current ? JSON.parse(JSON.stringify(current)) : null;
            const uncheckByValue = (node: any, leafValue: string): boolean => {
              if (!node) return false;
              let anyChecked = !!node.userChecked;
              if (String(node.value) === leafValue && node.userChecked) {
                node.userChecked = false;
                anyChecked = false;
              }
              if (Array.isArray(node.children) && node.children.length > 0) {
                let childAnyChecked = false;
                node.children.forEach(child => {
                  childAnyChecked = uncheckByValue(child, leafValue) || childAnyChecked;
                });
                node.userChecked = childAnyChecked;
                anyChecked = anyChecked || childAnyChecked;
              }
              return anyChecked;
            };
            if (cloned && Array.isArray(cloned.skillsets)) {
              cloned.skillsets.forEach(n => uncheckByValue(n, skill.value));
              const normalized = updateTreeNodes(pruneTree(cloned.skillsets));
              const patched = { ...cloned, skillsets: normalized };
              setValue('skillsetFilter', patched);
              setValue(candidateSearchFilterName.candidateSkillSet, patched);
            }
            // Trigger parent to reapply with updated form values
            onRemove('skillsetFilter');
          }}
          variant={selectedChipFilters.skillSet?.some((selected: any) => selected.name === skill.name) ? 'filled' : 'outlined'}
          color={selectedChipFilters.skillSet?.some((selected: any) => selected.name === skill.name) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      ))}
      {positions && (
        <Chip
          key={`positions-${positions.id || positions.value}`}
          label={`${positions.name}`}
          onClick={() => handleChipClick('positions', positions)}
          onDelete={() => {
            setValue('positions', null);
            onRemove('positions');
          }}
          variant={selectedChipFilters.positions?.some((selected: any) => selected.id === positions.id) ? 'filled' : 'outlined'}
          color={selectedChipFilters.positions?.some((selected: any) => selected.id === positions.id) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      )}
      {states && states.length > 0 && states.map((s: any, index: number) => (
        <Chip
          key={`state-${s.id}-${index}`}
          label={`${s.name}`}
          onClick={() => handleChipClick('states', s)}
          onDelete={() => {
            setValue('states', states.filter((item: any) => item.id !== s.id));
            onRemove('states');
          }}
          variant={selectedChipFilters.states?.some((selected: any) => selected.id === s.id) ? 'filled' : 'outlined'}
          color={selectedChipFilters.states?.some((selected: any) => selected.id === s.id) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' }, '&:hover .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      ))}
      {defaultTargetMargin && (defaultTargetMargin.min !== undefined || defaultTargetMargin.max !== undefined) && (
        <Chip
          label={(() => {
            const min = defaultTargetMargin.min;
            const max = defaultTargetMargin.max;
            
            if (min !== undefined && min !== null && max !== undefined && max !== null) {
              return `Default Target Margin (%): ${min} - ${max}`;
            } else if (min !== undefined && min !== null) {
              return `Default Target Margin (%): ${min}`;
            } else if (max !== undefined && max !== null) {
              return `Default Target Margin (%): ${max}`;
            }
            return '';
          })()}
          onClick={() => handleChipClick('defaultTargetMargin', defaultTargetMargin)}
          onDelete={() => {
            setValue('defaultTargetMargin', null);
            onRemove('defaultTargetMargin');
          }}
          variant={selectedChipFilters.defaultTargetMargin?.some((selected: any) => selected.min === defaultTargetMargin.min && selected.max === defaultTargetMargin.max) ? 'filled' : 'outlined'}
          color={selectedChipFilters.defaultTargetMargin?.some((selected: any) => selected.min === defaultTargetMargin.min && selected.max === defaultTargetMargin.max) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer' }}
        />
      )}
      {defaultWeeklyPay && (defaultWeeklyPay.min !== undefined || defaultWeeklyPay.max !== undefined) && (
        <Chip
          label={(() => {
            const min = defaultWeeklyPay.min;
            const max = defaultWeeklyPay.max;
            
            if (min !== undefined && min !== null && max !== undefined && max !== null) {
              return `Default Weekly Pay: $${min} - $${max}`;
            } else if (min !== undefined && min !== null) {
              return `Default Weekly Pay: $${min}`;
            } else if (max !== undefined && max !== null) {
              return `Default Weekly Pay: $${max}`;
            }
            return '';
          })()}
          onClick={() => handleChipClick('defaultWeeklyPay', defaultWeeklyPay)}
          onDelete={() => {
            setValue('defaultWeeklyPay', null);
            onRemove('defaultWeeklyPay');
          }}
          variant={selectedChipFilters.defaultWeeklyPay?.some((selected: any) => selected.min === defaultWeeklyPay.min && selected.max === defaultWeeklyPay.max) ? 'filled' : 'outlined'}
          color={selectedChipFilters.defaultWeeklyPay?.some((selected: any) => selected.min === defaultWeeklyPay.min && selected.max === defaultWeeklyPay.max) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      )}
      {payRangeMinMaxPercentage && (payRangeMinMaxPercentage.min !== undefined || payRangeMinMaxPercentage.max !== undefined) && (
        <Chip
          label={(() => {
            const min = payRangeMinMaxPercentage.min;
            const max = payRangeMinMaxPercentage.max;
            
            if (min !== undefined && min !== null && max !== undefined && max !== null) {
              return `Min/Max Percentage (%): ${min} - ${max}`;
            } else if (min !== undefined && min !== null) {
              return `Min/Max Percentage (%): ${min}`;
            } else if (max !== undefined && max !== null) {
              return `Min/Max Percentage (%): ${max}`;
            }
            return '';
          })()}
          onClick={() => handleChipClick('payRangeMinMaxPercentage', payRangeMinMaxPercentage)}
          onDelete={() => {
            setValue('payRangeMinMaxPercentage', null);
            onRemove('payRangeMinMaxPercentage');
          }}
          variant={selectedChipFilters.payRangeMinMaxPercentage?.some((selected: any) => selected.min === payRangeMinMaxPercentage.min && selected.max === payRangeMinMaxPercentage.max) ? 'filled' : 'outlined'}
          color={selectedChipFilters.payRangeMinMaxPercentage?.some((selected: any) => selected.min === payRangeMinMaxPercentage.min && selected.max === payRangeMinMaxPercentage.max) ? 'secondary' : 'default'}
          deleteIcon={<CloseIcon />}
          sx={{ cursor: 'pointer', '& .MuiChip-deleteIcon': { fontSize: '14px' } }}
        />
      )}
    </Box>
  );
};

// Component for Target Margins tab
const TargetMarginsTabContent: React.FC<{
  searchString: string;
  gridTag: string;
  gridType: GlobalSearchGridType;
  loading: boolean;
  getSearchResults: (params: unknown) => void;
  t: (key: string) => string;
}> = ({ searchString, gridTag, gridType, loading, getSearchResults, t }) => {
  const dispatch = useDispatch();
  const gridState = useSelector(selectGridState);
  const methods = useForm({
    defaultValues: {
      marginType: 'Order',
      division: [],
      facility: initialFacilityAggregateSearch,
      targetMarginSource: [],
      orderId: [],
      disciplineIds: [],
      skillsetFilter: null,
      positions: { id: 1, value: { min: 1, max: 999 }, name: '>0' },
      states: [],
      defaultTargetMargin: null,
      defaultWeeklyPay: null,
      payRangeMinMaxPercentage: null,
    }
  });
  
  const { handleSubmit } = methods;
  
  // State to track which chips are toggled on/off for subfiltering
  const [selectedChipFilters, setSelectedChipFilters] = useState<any>({});
  
  // Handle chip click to toggle selection state
  const handleChipClick = (filterKey: string, chipValue: any) => {
    const currentSelections = selectedChipFilters[filterKey] || [];
    const isCurrentlySelected = currentSelections.some((selected: any) => 
      JSON.stringify(selected) === JSON.stringify(chipValue)
    );
    
    let newSelections;
    if (isCurrentlySelected) {
      // Remove from selection
      newSelections = currentSelections.filter((selected: any) => 
        JSON.stringify(selected) !== JSON.stringify(chipValue)
      );
    } else {
      // Add to selection
      newSelections = [...currentSelections, chipValue];
    }
    
    const updatedSelectedChipFilters = {
      ...selectedChipFilters,
      [filterKey]: newSelections.length > 0 ? newSelections : undefined
    };
    
    setSelectedChipFilters(updatedSelectedChipFilters);
    
    // Apply the filter with the new selection state
    const currentValues = methods.getValues();
    handleSearch(currentValues, undefined, updatedSelectedChipFilters);
  };
  
  // Helper to determine if any skillset nodes are selected in the tree
  const hasSkillsetSelection = (filter: any): boolean => {
    if (!filter?.skillsets || !Array.isArray(filter.skillsets)) return false;
    let selected = false;
    const walk = (n: any) => {
      if (selected) return;
      if (n?.userChecked) selected = true;
      if (Array.isArray(n?.children)) n.children.forEach(walk);
    };
    filter.skillsets.forEach(walk);
    return selected;
  };
    
  const handleSearch = (values: any, sortParams?: any, chipFilters?: any) => {
    const facilitySelections = values?.facility?.selections ?? [];
    const orderIdSelections = Array.isArray(values?.orderId) ? values.orderId : [];
    
    const statesValue = values.states && values.states.length > 0 ? values.states.map((s: any) => s.value) : null;
    
    // Apply subfiltering based on selected chips
    const activeChipFilters = chipFilters || selectedChipFilters;
    
    const formatSkillsetFilter = (skillSetValue: any) => {
      if (!skillSetValue || !skillSetValue.skillsets || skillSetValue.skillsets.length === 0) {
        return {
          allowableMismatch: false,
          isExactMatch: false,
          operation: 'Or',
          skillsets: []
        };
      }

      const getCheckedSkillsets = (skillsets: any[]) => {
        const checkedSkillsets: any[] = [];
        skillsets.forEach(skill => {
          if (skill.userChecked) {
            const valueParts = skill.value.split('-');
            checkedSkillsets.push({
              disciplineId: valueParts[0] ? parseInt(valueParts[0]) : null,
              specialtyId: valueParts[1] ? parseInt(valueParts[1]) : null,
              subSpecialtyId: valueParts[2] ? parseInt(valueParts[2]) : null
            });
          }
          if (skill.children && skill.children.length > 0) {
            checkedSkillsets.push(...getCheckedSkillsets(skill.children));
          }
        });
        return checkedSkillsets;
      };

      const checkedSkillsets = getCheckedSkillsets(skillSetValue.skillsets);

      return {
        allowableMismatch: skillSetValue.allowableMismatch || false,
        isExactMatch: skillSetValue.isExactMatch || false,
        operation: skillSetValue.operation || 'Or',
        skillsets: checkedSkillsets
      };
    };

    const skillsetFilters = formatSkillsetFilter(values?.skillsetFilter);

    const filterPayload = {
        marginType: activeChipFilters.marginType?.length > 0 ? activeChipFilters.marginType[0] : values.marginType,
      // Apply subfiltering: if chips are selected, only include those items
      orderIds: activeChipFilters.orderId?.length > 0 
        ? activeChipFilters.orderId.map((item: any) => parseInt(String(item.id ?? item.value).replace(/[^0-9]/g, '')))
        : orderIdSelections.length > 0 ? orderIdSelections.map((item: any) => parseInt(String(item.id ?? item.value).replace(/[^0-9]/g, ''))) : null,
      divisionIds: activeChipFilters.division?.length > 0
        ? activeChipFilters.division.map((item: any) => parseInt(item.id.replace(/[^0-9]/g, '')))
        : values.division && values.division.length > 0 ? values.division.map(item => parseInt(item.id.replace(/[^0-9]/g, ''))) : null,
      targetMarginSourceIds: activeChipFilters.targetMarginSource?.length > 0
        ? activeChipFilters.targetMarginSource.map((item: any) => parseInt(String(item.id ?? item.value).replace(/[^0-9]/g, '')))
        : values.targetMarginSource && values.targetMarginSource.length > 0
          ? values.targetMarginSource.map(item => parseInt(String(item.id ?? item.value).replace(/[^0-9]/g, '')))
          : null,
      facilityIds: activeChipFilters.facility?.length > 0
        ? activeChipFilters.facility.map((item: any) => parseInt(String(item.id ?? item.fid ?? item.value).replace(/[^0-9]/g, '')))
        : facilitySelections.length > 0 ? facilitySelections.map((item: any) => parseInt(String(item.id ?? item.fid ?? item.value).replace(/[^0-9]/g, ''))) : null,
      skillsetFilters,
      positionsMin: values.positions ? values.positions.value.min : null,
      positionsMax: values.positions ? values.positions.value.max : null,
      states: activeChipFilters.states?.length > 0
        ? activeChipFilters.states.map((s: any) => s.value)
        : statesValue,
      defaultTargetMarginMin: values.defaultTargetMargin?.min !== undefined ? values.defaultTargetMargin.min : null,
      defaultTargetMarginMax: values.defaultTargetMargin?.max !== undefined ? values.defaultTargetMargin.max : null,
      defaultWeeklyPayMin: values.defaultWeeklyPay?.min !== undefined ? values.defaultWeeklyPay.min : null,
      defaultWeeklyPayMax: values.defaultWeeklyPay?.max !== undefined ? values.defaultWeeklyPay.max : null,
      percentageMin: values.payRangeMinMaxPercentage?.min !== undefined ? values.payRangeMinMaxPercentage.min : null,
      percentageMax: values.payRangeMinMaxPercentage?.max !== undefined ? values.payRangeMinMaxPercentage.max : null,
    };

    const isGridEvent = !!sortParams;
    const pageNumber = isGridEvent ? (sortParams?.pageNumber ?? 1) : 1;
    const pageSize = isGridEvent ? (sortParams?.pageSize || gridState?.pageSize || 100) : (gridState?.pageSize || 100);

    dispatch(serviceActions.getTargetMarginsSearchResults({
      keyword: searchString || '',
      filter: filterPayload,
      pageNumber,
      pageSize,
      sortedColumn: sortParams?.sortModel || gridState?.sortedColumn || { column: 'RowKey', direction: 'asc' },
      translation: t,
    }));
    if (!isGridEvent) {
      dispatch(gridStateActions.setPageNumber(1));
    }
  };

  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    isInitialMount.current = false;
  }, []);
  React.useEffect(() => {
    dispatch(globalSearchActions.clearSearchResults());
  }, [dispatch]);


  const handleChipRemove = (field: string) => {
    // Ensure subfilter selection is also cleared when the chip is removed
    const updatedSelectedChipFilters = { ...selectedChipFilters };
    if (updatedSelectedChipFilters[field]) {
      delete updatedSelectedChipFilters[field];
    }
    setSelectedChipFilters(updatedSelectedChipFilters);

    const currentValues = methods.getValues();
    handleSearch(currentValues, undefined, updatedSelectedChipFilters);
  };

  // Custom getSearchResults function that applies current filters when sorting
  const handleGridSearchResults = (params: any) => {
    const currentValues = methods.getValues();
    handleSearch(currentValues, params);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSearch)}>
        <Box id="details-page-paper" sx={{ marginLeft: '12px', marginRight: '12px', minHeight: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column' }}>
        <Box id="details-page-filters" sx={{ marginLeft: '-4px', marginRight: '-4px' }}>
            <TargetMarginSearchFilters onApplyFilter={(filters) => handleSearch(filters)} />
          </Box>
        <Box sx={{ marginTop: 2, marginLeft: '-12px', marginRight: '-12px' }}>
          <Box id="details-page-chips" sx={{ paddingLeft: 0, paddingRight: 0 }}>
              <TargetMarginChips onRemove={handleChipRemove} selectedChipFilters={selectedChipFilters} handleChipClick={handleChipClick} />
            </Box>
            <SearchResultsGrid
              searchCategory={SearchType.targetmargins}
              searchString={searchString}
              gridTitle="Target Margins"
              gridTag={`${gridTag}-target-margins`}
              gridType={GlobalSearchGridType[gridType]}
              loading={loading}
              getSearchResults={handleGridSearchResults}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export const MarginSearchWrapper: React.FC<MarginSearchWrapperProps> = ({
  searchString,
  gridTag,
  loading,
  getSearchResults,
  gridType,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { drawerData } = useSelector(selectSearchResults);
  const navigate = useNavigate();
  const location = useLocation();

  // Set default tab to Orders (index 1) if no tab is specified in URL
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.has('tab')) {
      searchParams.set('tab', '1');
      navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true, state: location.state });
    }
  }, []); // Run only once on mount

  // Override: For Margin Orders tab only, call /search API via getAMIECategorySearch
  const getOrderSearchResults = React.useCallback(
    ({ pageNumber, pageSize, sortModel, multiSortedColumns, filter }: any) => {
      dispatch(
        globalSearchActions.getSearchResults({
          searchType: SearchType.orderpaypackage,
          keyword: searchString || '',
          pageNumber: pageNumber || 1,
          pageSize: pageSize || 100,
          sortedColumn: sortModel,
          multiSortedColumns,
          filter: filter || {},
          translation: t,
          navigationDataLoad: true,
        }),
      );
    },
    [dispatch, searchString, t],
  );

  const tabs = React.useMemo(() => [
    { 
      label: 'Placements', 
      component: (
        <SearchResultsGrid
          key={`${gridTag}-placements`}
          searchCategory={SearchType.placement}
          searchString={searchString}
          gridTitle="Placements"
          gridTag={`${gridTag}-placements`}
          gridType={GlobalSearchGridType[gridType]}
          loading={loading}
          getSearchResults={getSearchResults}
        />
      )
    },
    { 
      label: 'Orders', 
      component: (
        <OrdersTabContent
          key={`${gridTag}-orders`}
          searchString={searchString}
          gridTag={`${gridTag}-orders`}
          gridType={gridType}
          loading={loading}
          getSearchResults={getOrderSearchResults}
          t={t}
        />
      )
    },
    { 
      label: 'Target Margins', 
      component: (
        <TargetMarginsTabContent
          key={`${gridTag}-targetmargins`}
          searchString={searchString}
          gridTag={`${gridTag}-targetmargins`}
          gridType={gridType}
          loading={loading}
          getSearchResults={getSearchResults}
          t={t}
        />
      )
    },
  ], [searchString, gridTag, gridType, loading, getSearchResults, getOrderSearchResults, t]);

  return (
    <Paper id="margin-page-paper" classes={{ root: classes.root }} sx={{ backgroundColor: theme.palette.framework.system.whisper }}>
      <LayoutGrid container direction="row" justifyContent="space-between" classes={{ container: classes.container }}>
        <LayoutGridItem
          container
          direction="column"
          spacing={2}
          classes={{ root: classes.mainPanelRoot, container: classes.container }}
        >
          <LayoutGridItem id="margin-page-title" container direction="row" justifyContent="space-between">
            <LayoutGridItem>
              <Box className="page-title">
                {t('search.globalSearch.margin.title')}
              </Box>
            </LayoutGridItem>
          </LayoutGridItem>
          
          <LayoutGridItem id="margin-page-tabs" xs={12}>
            <Paper className="paper-2" sx={{ minHeight: 'calc(100vh - 170px)', paddingLeft: '12px', paddingRight: '12px', paddingTop: '12px', paddingBottom: '12px' }}>
              <GenericTabs
                tabs={tabs}
                value={undefined}
                modifyUrl={true}
                dense={true}
                hideHorizontalBorder={false}
                onTabChanged={(newTab) => {
                  // Reset grid state when switching tabs to prevent MUI errors and reset page numbers
                  dispatch(gridStateActions.reset());
                }}
                customTabPanel={{
                  backgroundColor: '#ffffff',
                  minHeight: 'calc(100vh - 170px)',
                  padding: '0px',
                }}
              />
            </Paper>
          </LayoutGridItem>
        </LayoutGridItem>
      </LayoutGrid>
      
      <DrawerFitted
        onDrawerClose={() => {
          dispatch(globalSearchActions.setDrawerData({ open: false, data: undefined }));
        }}
        width={450}
        top={0}
        backgroundColor={theme.palette.framework.system.whisper}
        open={drawerData?.open ?? false}
        shadow={false}
      >
        {drawerData?.open && drawerData?.data ? (
          <SearchDrawerContent category="margin" data={drawerData?.data} />
        ) : null}
      </DrawerFitted>
    </Paper>
  );
};
